import {
  Badge,
  Button,
  Card,
  CellText,
  DataTable,
  DatePicker,
  EmptyState,
  FormField,
  List,
  ListRow,
  ListToolbar,
  MoneyInput,
  Section,
  Text,
  todayIso,
  useToast,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage, formErrors } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useSession } from '../../identity/session';
import { usePrices, useRemovePrice, useSetPrice, type PriceListItem } from '../api';
import { formatEuro } from '../format';
import styles from './SalesPages.module.css';

/** Price list per article with price history (SAL-009). */
export function PricesPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const prices = usePrices();
  const columnSort = useColumnSort();
  const [editing, setEditing] = useState<PriceListItem | null>(null);
  const rows = (prices.data ?? []).filter((a) => !a.archived || a.jars > 0);

  const columns: DataTableColumn<PriceListItem>[] = [
    {
      key: 'article',
      header: t('sales.columns.article'),
      primary: true,
      cell: (a: PriceListItem) => (
        <span>
          {a.article.name}
          {a.archived && (
            <>
              {' '}
              <Badge size="sm">{t('crm.archived')}</Badge>
            </>
          )}
        </span>
      ),
      sortValue: (a: PriceListItem) => a.article.name,
    },
    {
      key: 'stock',
      header: t('sales.prices.stock'),
      align: 'end',
      mobile: 'subtitle',
      cell: (a) => (
        <CellText
          main={
            <Text as="span" numeric>
              {a.jars}
            </Text>
          }
          sub={a.reserved > 0 ? t('sales.prices.reserved', { count: a.reserved }) : null}
        />
      ),
      mobileCell: (a) =>
        a.reserved > 0
          ? t('sales.prices.inStockReserved', { count: a.jars, reserved: a.reserved })
          : t('sales.prices.inStock', { count: a.jars }),
      sortValue: (a) => a.jars,
    },
    {
      key: 'price',
      header: t('sales.prices.current'),
      align: 'end',
      mobile: 'subtitle',
      cell: (a) =>
        a.current_cents != null ? (
          <Text as="span" numeric>
            {formatEuro(a.current_cents)}
          </Text>
        ) : (
          <Badge tone="warning" size="sm">
            {t('sales.prices.none')}
          </Badge>
        ),
      sortValue: (a) => a.current_cents,
    },
    {
      key: 'since',
      header: t('sales.prices.since'),
      mobile: 'hidden',
      cell: (a) => {
        const current = a.history.find((h) => h.valid_from <= todayIso());
        const next = [...a.history].reverse().find((h) => h.valid_from > todayIso());
        return (
          <CellText
            main={current ? formatDate(current.valid_from) : '–'}
            sub={
              next
                ? t('sales.prices.upcoming', {
                    price: formatEuro(next.price_cents),
                    date: formatDate(next.valid_from),
                  })
                : null
            }
          />
        );
      },
      sortValue: (a) => a.history.find((h) => h.valid_from <= todayIso())?.valid_from,
    },
  ];

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      <ListToolbar sort={sortMenu} />
      <Card padding="none">
        {prices.error && (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
            {errorMessage(prices.error)}
          </Text>
        )}
        <DataTable
          caption={t('sales.prices.caption')}
          rows={rows}
          rowKey={(a) => a.article.id}
          {...columnSort}
          loading={prices.isPending}
          columns={columns}
          {...(can('sales.write')
            ? {
                rowActions: rowMenu.table<PriceListItem>(
                  (a) => a.article.name,
                  (a) => [
                    {
                      key: 'price',
                      label: t('sales.prices.setPrice'),
                      onSelect: () => setEditing(a),
                    },
                  ],
                ),
              }
            : {})}
          empty={
            <EmptyState
              title={t('sales.prices.emptyTitle')}
              description={t('sales.prices.empty')}
            />
          }
        />
      </Card>
      {editing && (
        <PriceDialog
          item={rows.find((r) => r.article.id === editing.article.id) ?? editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function PriceDialog({ item, onClose }: { item: PriceListItem; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const set = useSetPrice();
  const remove = useRemovePrice();
  const [price, setPrice] = useState<number | null>(item.current_cents ?? null);
  const [from, setFrom] = useState(todayIso());
  const errors = formErrors(set.error ?? remove.error, { price: ['price.'] });
  return (
    <FormDialog
      open
      onClose={onClose}
      title={t('sales.prices.dialogTitle', { name: item.article.name })}
      submitLabel={t('common.save')}
      pending={set.isPending}
      error={errors.general}
      onSubmit={() =>
        set.mutate(
          { article: item.article.id, price_cents: price ?? -1, valid_from: from },
          {
            onSuccess: () => {
              toast.show(t('sales.prices.saved'));
              onClose();
            },
          },
        )
      }
    >
      <div className={styles.row}>
        <FormField label={t('sales.prices.price')} required error={errors.field.price}>
          {(p) => (
            <MoneyInput
              {...p}
              value={price}
              onChange={setPrice}
              labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
            />
          )}
        </FormField>
        <FormField label={t('sales.prices.validFrom')} required>
          {(p) => <DatePicker {...p} value={from} onChange={setFrom} />}
        </FormField>
      </div>
      {item.history.length > 0 && (
        <Section title={t('sales.prices.history')}>
          <List>
            {item.history.map((h) => (
              <ListRow
                key={h.valid_from}
                title={formatEuro(h.price_cents)}
                description={t('sales.prices.from', { date: formatDate(h.valid_from) })}
                trailing={
                  <Button
                    variant="plain"
                    size="sm"
                    aria-label={t('sales.prices.removeEntry', { date: formatDate(h.valid_from) })}
                    loading={remove.isPending && remove.variables?.valid_from === h.valid_from}
                    onClick={() =>
                      remove.mutate(
                        { article: item.article.id, valid_from: h.valid_from },
                        { onSuccess: () => toast.show(t('sales.prices.removed')) },
                      )
                    }
                  >
                    {t('sales.prices.remove')}
                  </Button>
                }
              />
            ))}
          </List>
        </Section>
      )}
    </FormDialog>
  );
}
